import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Search, Play, CheckCircle2, RotateCcw, Package, Layers, CalendarClock, CornerDownRight, ArrowRightCircle } from "lucide-react";

export const PROD_STATUS = {
  da_iniziare: { label: "Da iniziare", style: "bg-[#F4EFEA] text-[#5A493C] border-[#D5C5B5]" },
  in_corso: { label: "In corso", style: "bg-[#EBF3FA] text-[#1D4ED8] border-[#96C2EC]" },
  completata: { label: "Completata", style: "bg-[#EFF7F2] text-[#166534] border-[#9AD4AC]" },
};

const FILTERS = [
  { key: "", label: "Tutte" },
  { key: "da_iniziare", label: "Da iniziare" },
  { key: "in_corso", label: "In corso" },
  { key: "completata", label: "Completate" },
];

const DEPT_LABEL = { taglio: "Taglio", cucito: "Cucito", rivestimento: "Rivestimento", tappezzeria: "Tappezzeria", montaggio: "Montaggio" };

function dueState(r) {
  if (!r.due_date || r.status === "completata") return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(r.due_date); due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff < 0) return { kind: "overdue", label: diff === -1 ? "Scaduta ieri" : `Scaduta da ${-diff}g` };
  if (diff === 0) return { kind: "soon", label: "Scade oggi" };
  if (diff <= 2) return { kind: "soon", label: `Tra ${diff}g` };
  return null;
}

export default function ProductionOrders({ department, title, subtitle, icon: Icon }) {
  const EMPTY = { product: "", material: "", quantity: "", due_date: "", notes: "", status: "da_iniziare" };
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [ed, setEd] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ department });
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/production-orders?${params.toString()}`);
      setRows(data);
    } catch (e) { toast.error(apiErr(e)); }
  }, [department, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => setEd((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!ed.product.trim()) { toast.error("Indica cliente / prodotto"); return; }
    const body = { department, product: ed.product, material: ed.material || null, status: ed.status,
      quantity: ed.quantity === "" ? null : parseInt(ed.quantity, 10), due_date: ed.due_date || null, notes: ed.notes || null };
    try {
      if (ed.id) await api.put(`/production-orders/${ed.id}`, body);
      else await api.post("/production-orders", body);
      toast.success("Commessa salvata");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const changeStatus = async (row, st) => {
    try {
      const { data } = await api.patch(`/production-orders/${row.id}/status`, { status: st });
      if (st === "completata" && data.next_code) toast.success(`Completata → passata a ${DEPT_LABEL[data.next_department] || data.next_department} (${data.next_code})`);
      else toast.success(`Stato: ${PROD_STATUS[st].label}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare la commessa ${row.code}?`)) return;
    try { await api.delete(`/production-orders/${row.id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid={`prod-page-${department}`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-lm-text flex items-center gap-2">{Icon && <Icon className="w-7 h-7 text-lm-sand" />} {title}</h1>
          <p className="text-sm text-lm-muted">{subtitle}</p>
        </div>
        <Button data-testid="new-prod-order-btn" onClick={() => { setEd({ ...EMPTY }); setOpen(true); }} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">
          <Plus className="w-4 h-4 mr-2" /> Nuova commessa
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key || "all"} data-testid={`prod-filter-${f.key || "all"}`} onClick={() => setStatus(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${status === f.key ? "bg-lm-brown text-lm-cream border-lm-brown" : "bg-white border-lm-sandsoft text-lm-text2 hover:bg-lm-sandsoft"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-lm-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="prod-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice, prodotto, materiale…" className="pl-8 bg-white w-64 max-w-full" />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="lm-card p-10 text-center text-lm-muted text-sm" data-testid="prod-empty">Nessuna commessa. Crea la prima con "Nuova commessa".</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((r) => {
            const ds = dueState(r);
            return (
            <div key={r.id} data-testid={`prod-order-${r.code}`} className={`lm-card p-5 flex flex-col ${ds?.kind === "overdue" ? "!border-[#F8B4B4] ring-1 ring-[#F8B4B4]" : ds?.kind === "soon" ? "!border-[#F3D295] ring-1 ring-[#F3D295]" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-mono text-xs font-bold text-lm-brown">{r.code}</span>
                  <h3 className="font-display text-xl text-lm-text truncate">{r.product}</h3>
                </div>
                <span data-testid={`prod-status-${r.code}`} className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-md border ${PROD_STATUS[r.status]?.style}`}>{PROD_STATUS[r.status]?.label || r.status}</span>
              </div>
              <div className="mt-3 space-y-1 text-sm text-lm-text2">
                {r.material && <p className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-lm-sand" /> {r.material}</p>}
                {r.quantity != null && <p className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-lm-sand" /> {r.quantity} pezzi</p>}
                {r.due_date && (
                  <p className={`flex items-center gap-1.5 ${ds?.kind === "overdue" ? "text-[#991B1B] font-semibold" : ds?.kind === "soon" ? "text-[#9A6B00] font-semibold" : ""}`}>
                    <CalendarClock className="w-3.5 h-3.5 text-lm-sand" /> Prevista {fmtDate(r.due_date)}
                    {ds && <span data-testid={`prod-due-${r.code}`} className={`ml-1 text-[11px] px-1.5 py-0.5 rounded border ${ds.kind === "overdue" ? "bg-[#FDF2F2] text-[#991B1B] border-[#F8B4B4]" : "bg-[#FEF8EC] text-[#9A6B00] border-[#F3D295]"}`}>{ds.label}</span>}
                  </p>
                )}
                {r.source_code && <p className="text-xs text-lm-muted flex items-center gap-1.5"><CornerDownRight className="w-3.5 h-3.5" /> da {r.source_code}</p>}
                {r.next_code && <p className="text-xs text-lm-muted flex items-center gap-1.5"><ArrowRightCircle className="w-3.5 h-3.5" /> passata a {DEPT_LABEL[r.next_department] || r.next_department} ({r.next_code})</p>}
              </div>
              {r.notes && <p className="text-sm text-lm-muted mt-2 line-clamp-3">{r.notes}</p>}

              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-lm-sandsoft">
                {r.status === "da_iniziare" && (
                  <Button size="sm" data-testid={`prod-start-${r.code}`} onClick={() => changeStatus(r, "in_corso")} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><Play className="w-3.5 h-3.5 mr-1.5" /> Avvia</Button>
                )}
                {r.status === "in_corso" && (
                  <Button size="sm" data-testid={`prod-complete-${r.code}`} onClick={() => changeStatus(r, "completata")} className="bg-[#166534] hover:bg-[#12512a] text-white"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Completa</Button>
                )}
                {r.status === "completata" && (
                  <Button size="sm" variant="outline" data-testid={`prod-reopen-${r.code}`} onClick={() => changeStatus(r, "in_corso")} className="border-lm-sand text-lm-brown"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Riapri</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setEd({ ...EMPTY, ...r, quantity: r.quantity ?? "", material: r.material ?? "", due_date: r.due_date ?? "", notes: r.notes ?? "" }); setOpen(true); }} className="border-lm-sand text-lm-brown"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifica</Button>
                <Button size="sm" variant="outline" onClick={() => del(r)} className="border-lm-sand text-[#991B1B] ml-auto"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto bg-lm-cream" data-testid="prod-order-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl text-lm-brown">{ed.id ? `Modifica ${ed.code}` : `Nuova commessa · ${title}`}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 py-2">
              <div className="sm:col-span-2"><Label className="text-xs text-lm-text2">Cliente / Prodotto *</Label><Input data-testid="prod-product" value={ed.product} onChange={(e) => set("product", e.target.value)} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs text-lm-text2">Materiale</Label><Input data-testid="prod-material" value={ed.material} onChange={(e) => set("material", e.target.value)} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs text-lm-text2">Quantità (pezzi)</Label><Input data-testid="prod-quantity" type="number" min="0" value={ed.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs text-lm-text2">Data prevista</Label><Input data-testid="prod-due-date" type="date" value={ed.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs text-lm-text2">Stato</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {Object.entries(PROD_STATUS).map(([k, v]) => (
                    <button key={k} type="button" data-testid={`prod-set-status-${k}`} onClick={() => set("status", k)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${ed.status === k ? "bg-lm-brown text-lm-cream border-lm-brown" : "bg-white border-lm-sandsoft text-lm-text2"}`}>{v.label}</button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2"><Label className="text-xs text-lm-text2">Note</Label><Textarea data-testid="prod-notes" value={ed.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1 bg-white" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="border-lm-sand text-lm-brown">Annulla</Button>
              <Button data-testid="save-prod-order-btn" onClick={save} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
